import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import delve from 'dlv';

import useMedia from 'hooks/useMedia';
import { EntityContext, ContextTopics } from 'context/entityContext';
import { FeedTypes } from 'topics';

import DSPSelectorLoader from 'components/utility/ContentLoader/DSPSelectorLoader/DSPSelectorLoader';

import {
  shouldLoadSelectedCustomers,
  loadSelectedCustomers,
  saveSelectedCustomers,
} from 'redux/reducers/selectedCustomers';

import {
  Container,
  Header,
  HeaderContent,
  Label,
  Title,
  Content,
  Footer,
  ResetButton,
  SaveButton,
  Info,
  ResetButtonContainer,
  StyledCloseButton,
  WarningMessage,
} from './DSPSelector.style';
import DSPList from './DSPList/DSPList';
import SelectedDSPList from './SelectedDSPList/SelectedDSPList';
import { ReactComponent as CloseIcon } from 'icons/icon-close-large.svg';
import { withGeneralSettings } from 'components/hocs/withGeneralSettings';
import {
  selectCustomersByFeedType,
  loadCustomers,
} from 'redux/reducers/customers';
import { AllTopicTypes } from 'topics';
import { TopListsContext } from 'context/topLists';

const MinDSPInfo: React.FC = () => (
  <Info>
    <p>
      You can&#39;t delete all DSPs from the list. To remove the last DSP, add a
      new one first.
    </p>
  </Info>
);

interface IMaxDSPInfoProps {
  dspCountLimit: number;
}

const MaxDSPInfo: React.FC<IMaxDSPInfoProps> = (props) => {
  const { dspCountLimit } = props;
  return (
    <Info>
      <p>
        This list has reached the limit of {dspCountLimit} DSPs. To add any new
        DSP, remove one first.
      </p>
    </Info>
  );
};

const MAX_SELECTED_CUSTOMERS = 8;

function topicToDeprecatedCustomersKey(
  topic: ContextTopics
): string | undefined {
  if (!topic) {
    return undefined;
  }

  switch (true) {
    case topic.some((t: FeedTypes) => t === 'downloads'):
      return 'downloadCustomers';
    case topic.some((t: FeedTypes) => t === 'streaming'):
      return 'streamingCustomers';
    default:
      return undefined;
  }
}

interface IDSPSelectorProps {
  generalSettings: any;
  toggleDSPSelector: any;
  topDSPsByCountries?: any;
  additionalType?: boolean | string;
}

const DSPSelector: React.FC<IDSPSelectorProps> = (props) => {
  const {
    generalSettings,
    toggleDSPSelector,
    additionalType,
    topDSPsByCountries,
  } = props;

  const context = useContext(EntityContext);
  const topListsContext = useContext(TopListsContext);
  const { topic: contextTopic } = context;
  const topic = topListsContext?.topic?.length
    ? (topListsContext?.topic as any)
    : contextTopic;

  const activeTopic = useSelector((state: any) => {
    const selectedCustomers = state.selectedCustomers.value;
    return topic.find((topicItem: AllTopicTypes) => {
      const feed = topicItem;
      return feed && feed in selectedCustomers;
    });
  });

  const activeTopicUpdated = `${activeTopic}${
    additionalType ? `-${additionalType}` : ''
  }`;

  const selectedCustomers = useSelector((state: any) => {
    const selectedCustomers = state.selectedCustomers.value;
    if (activeTopic) {
      return selectedCustomers[activeTopicUpdated as string];
    }
  });

  const selectedCustomersRejected = useSelector(
    (state: any) => state.selectedCustomers.rejected
  );

  const customerList = useSelector(
    useCallback(selectCustomersByFeedType(topic as any), [activeTopic])
  );

  const [tempSelectedCustomers, setTempSelectedCustomers] = useState<any[]>();

  const isSmallScreen = useMedia('(min-width: 50em)');

  const dispatch = useDispatch();

  // Only run on mount
  useEffect(() => {
    dispatch(loadCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (customerList && customerList.length > 0) {
      if (dispatch(shouldLoadSelectedCustomers())) {
        dispatch(loadSelectedCustomers());
      }
    }
  }, [dispatch, customerList]);

  const deprecatedKey: string = topicToDeprecatedCustomersKey(topic) || '';

  const defaultCustomers = delve(generalSettings, [
    'data',
    'defaultCustomers',
    deprecatedKey,
  ]);

  const allCustomers = useMemo(() => {
    const key = topicToDeprecatedCustomersKey(topic);

    function addCustomerColor(customer: any): any | undefined {
      if (!key) {
        return;
      }

      const color =
        generalSettings.data.customerColors[key][customer.name] ||
        customer.color;

      return { ...customer, color };
    }

    function sortByName(a: any, b: any): number {
      // ignore upper and lowercase
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      return 0;
    }

    if (customerList && customerList.length > 0 && generalSettings.success) {
      return (customerList as any).map(addCustomerColor).sort(sortByName);
    }
  }, [customerList, generalSettings, topic]);

  const selectionLimit = useMemo(() => {
    if (allCustomers) {
      return Math.min(allCustomers.length, MAX_SELECTED_CUSTOMERS);
    }

    return MAX_SELECTED_CUSTOMERS;
  }, [allCustomers]);

  const minLimitReached = useMemo(() => {
    if (selectedCustomers) {
      return selectedCustomers.length <= 1;
    }

    return false;
  }, [selectedCustomers]);

  const maxLimitReached = useMemo(() => {
    if (selectedCustomers) {
      return selectedCustomers.length >= selectionLimit;
    }

    return false;
  }, [selectedCustomers, selectionLimit]);

  const showCount = useMemo(() => {
    if (allCustomers) {
      return allCustomers.length > MAX_SELECTED_CUSTOMERS;
    }

    return true;
  }, [allCustomers]);

  const isDefaultSetting = useMemo(() => {
    return isEqual(selectedCustomers, defaultCustomers);
  }, [selectedCustomers, defaultCustomers]);

  const isDefaultSettingByCountries = useMemo(() => {
    return isEqual(selectedCustomers, topDSPsByCountries);
  }, [selectedCustomers, topDSPsByCountries]);

  const resetCustomers = useCallback(() => {
    if (activeTopicUpdated !== undefined) {
      const feedType = activeTopicUpdated;
      dispatch(saveSelectedCustomers(defaultCustomers, feedType as string));
    }
  }, [dispatch, defaultCustomers, activeTopicUpdated]);

  const resetCustomersByCountries = useCallback(() => {
    if (activeTopicUpdated !== undefined) {
      const feedType = activeTopicUpdated;
      dispatch(saveSelectedCustomers(topDSPsByCountries, feedType as string));
    }
  }, [dispatch, topDSPsByCountries, activeTopicUpdated]);

  const toggleCustomer = useCallback(
    (customer) => {
      // if the customer is already selected
      if (selectedCustomers.includes(customer)) {
        // and we have more than 1 selected customers
        if (!minLimitReached && activeTopicUpdated !== undefined) {
          // remove the customer
          dispatch(
            saveSelectedCustomers(
              selectedCustomers.filter((el: any) => el !== customer),
              activeTopicUpdated as string
            )
          );
        }
        // if it is not selected yet
      } else {
        // and we haven't reached the limit
        if (!maxLimitReached && activeTopicUpdated !== undefined) {
          // add the customer]
          dispatch(
            saveSelectedCustomers(
              [...selectedCustomers, customer],
              activeTopicUpdated as string
            )
          );
        }
      }
    },
    [
      dispatch,
      minLimitReached,
      maxLimitReached,
      selectedCustomers,
      activeTopicUpdated,
    ]
  );

  const saveTempCustomers = useCallback(() => {
    if (activeTopicUpdated && tempSelectedCustomers) {
      dispatch(
        saveSelectedCustomers(
          tempSelectedCustomers,
          activeTopicUpdated as string
        )
      );
    }
    setTempSelectedCustomers(undefined);
  }, [dispatch, tempSelectedCustomers, activeTopicUpdated]);

  const changeDSPOrder = useCallback(
    (start, end) => {
      const temp = [...(tempSelectedCustomers || selectedCustomers)];
      const customer = temp[start];

      /** Move the item from its old position to the new position by changing the order of the array. */
      temp.splice(start, 1);
      temp.splice(end, 0, customer);

      setTempSelectedCustomers(temp);
    },
    [selectedCustomers, tempSelectedCustomers]
  );

  if (!allCustomers || !defaultCustomers || !selectedCustomers) {
    return (
      <Container>
        <DSPSelectorLoader />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Label>{topic.join(' ')} DSPs</Label>
          <Title>Customize list</Title>
          {selectedCustomersRejected && (
            <WarningMessage>
              Your selected DSP settings failed to load, so unfortunately we
              cannot retrieve or save your personal settings. Please try to
              refresh or{' '}
              <a href="mailto:opus-support@wmg.com">contact Opus Support</a>.
            </WarningMessage>
          )}
          <p>
            {selectionLimit >= MAX_SELECTED_CUSTOMERS &&
              `Select up to ${MAX_SELECTED_CUSTOMERS} ${topic.join(
                ' '
              )} DSPs to visualize in this graph. Drag
          each selected DSP up or down to customize the list order. Any change is applied instantly and it
          affects all graphs that show a selection of ${topic.join(' ')} DSPs.`}
            {selectionLimit < MAX_SELECTED_CUSTOMERS &&
              `Customize the ${topic} DSPs to visualize in this graph. Drag each selected DSP up or down to change the list order.
              Any change is applied instantly and it affects all graphs that show a selection of ${topic} DSPs.`}
          </p>
        </HeaderContent>
        {!isSmallScreen && (
          <StyledCloseButton onClick={toggleDSPSelector}>
            <CloseIcon />
          </StyledCloseButton>
        )}
      </Header>
      <Content>
        <div>
          <Label>All DSPs</Label>
          <DSPList
            allDSPs={allCustomers}
            selectedDSPs={selectedCustomers}
            toggleDSP={toggleCustomer}
            dspLimitReached={maxLimitReached}
          />
          <div>
            {minLimitReached && <MinDSPInfo />}
            {maxLimitReached && <MaxDSPInfo dspCountLimit={selectionLimit} />}
          </div>
        </div>
        <div>
          <Label>
            Selected in view
            {showCount &&
              ` (${selectedCustomers.length} / ${selectionLimit} selected)`}
          </Label>
          <SelectedDSPList
            allDSPs={allCustomers}
            selectedDSPs={tempSelectedCustomers || selectedCustomers}
            toggleDSP={toggleCustomer}
            saveDSPs={saveTempCustomers}
            changeDSPOrder={changeDSPOrder}
          />
          <ResetButtonContainer>
            {isSmallScreen && (
              <>
                {!!topDSPsByCountries && (
                  <ResetButton
                    onClick={resetCustomersByCountries}
                    disabled={isDefaultSettingByCountries}
                  >
                    Reset to Top DSP default
                  </ResetButton>
                )}
                <ResetButton
                  onClick={resetCustomers}
                  disabled={isDefaultSetting}
                >
                  Reset to {!!topDSPsByCountries && 'Global'} default
                </ResetButton>
              </>
            )}
          </ResetButtonContainer>
          {isSmallScreen && (
            <div style={{ marginTop: 'auto' }}>
              <SaveButton onClick={toggleDSPSelector}>Done</SaveButton>
            </div>
          )}
        </div>
      </Content>
      <Footer>
        {!isSmallScreen && (
          <div>
            {!!topDSPsByCountries && (
              <ResetButton
                onClick={resetCustomersByCountries}
                disabled={isDefaultSetting}
              >
                Reset to Top DSP default
              </ResetButton>
            )}
            <ResetButton onClick={resetCustomers} disabled={isDefaultSetting}>
              Reset to {!!topDSPsByCountries && 'Global'} default
            </ResetButton>
          </div>
        )}
        {!isSmallScreen && (
          <div>
            <SaveButton onClick={toggleDSPSelector}>Done</SaveButton>
          </div>
        )}
      </Footer>
    </Container>
  );
};

export default withGeneralSettings(DSPSelector);
